/**
 * ReminderContext
 * Provides reminder state and functions throughout the app
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import reminderService from '../services/reminderService';

const ReminderContext = createContext();

export const useReminder = () => {
  const context = useContext(ReminderContext);
  if (!context) {
    throw new Error('useReminder must be used within ReminderProvider');
  }
  return context;
};

export const ReminderProvider = ({ children }) => {
  const [activeReminder, setActiveReminder] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [activeTrackers, setActiveTrackers] = useState([]);
  const [initialized, setInitialized] = useState(false);

  // Load initial data only if user is authenticated
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      loadPreferences();
      loadConfigs();
      loadActiveTrackers();
      initializeDefaults();
    }
  }, []);

  // Poll for active reminders every 15 seconds
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    
    const pollActiveReminders = async () => {
      try {
        const instances = await reminderService.getActiveInstances();
        // Only show if we don't already have an active reminder displayed
        if (instances && instances.length > 0 && !activeReminder) {
          setActiveReminder(instances[0]);
        }
      } catch (error) {
        console.error('Failed to poll for active reminders:', error);
      }
    };
    
    // Initial check
    pollActiveReminders();
    
    // Poll every 15 seconds
    const interval = setInterval(pollActiveReminders, 15000);
    
    return () => clearInterval(interval);
  }, [activeReminder]); // Re-run when activeReminder changes

  // ADDED: Check for overdue tasks on mount and periodically
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    
    const checkOverdue = async () => {
      try {
        await reminderService.checkOverdueTasks();
        // Don't need to do anything with the result here,
        // the active reminder polling will pick it up
      } catch (error) {
        // Silently fail - don't bother the user
        console.debug('Overdue check error (expected if no overdue tasks):', error);
      }
    };
    
    // Check immediately on mount
    checkOverdue();
    
    // Check every 30 minutes for overdue tasks
    const interval = setInterval(checkOverdue, 30 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []); // Only run on mount

  const initializeDefaults = async () => {
    try {
      await reminderService.initializeDefaults();
      setInitialized(true);
    } catch (error) {
      console.error('Failed to initialize reminders:', error);
      // Don't throw - just log and continue
      setInitialized(false);
    }
  };

  const loadPreferences = async () => {
    try {
      const prefs = await reminderService.getPreferences();
      setPreferences(prefs);
    } catch (error) {
      console.error('Failed to load preferences:', error);
      // Set default preferences if load fails
      setPreferences(null);
    }
  };

  const loadConfigs = async () => {
    try {
      const configList = await reminderService.getConfigs();
      setConfigs(configList);
    } catch (error) {
      console.error('Failed to load configs:', error);
      // Set empty array if load fails
      setConfigs([]);
    }
  };

  const loadActiveTrackers = async () => {
    try {
      const trackers = await reminderService.getActiveTrackers();
      setActiveTrackers(trackers);
    } catch (error) {
      // Silently fail for 404 errors (endpoint not implemented)
      if (error.response?.status === 404) {
        setActiveTrackers([]);
        return;
      }
      console.error('Failed to load trackers:', error);
      // Set empty array if load fails
      setActiveTrackers([]);
    }
  };

  const updatePreferences = async (updates) => {
    try {
      const result = await reminderService.updatePreferences(updates);
      setPreferences(result.preferences);
      return result;
    } catch (error) {
      console.error('Failed to update preferences:', error);
      throw error;
    }
  };

  const createConfig = async (config) => {
    try {
      const newConfig = await reminderService.createConfig(config);
      setConfigs([...configs, newConfig]);
      return newConfig;
    } catch (error) {
      console.error('Failed to create config:', error);
      throw error;
    }
  };

  const updateConfig = async (configId, updates) => {
    try {
      const updated = await reminderService.updateConfig(configId, updates);
      setConfigs(configs.map(c => c.id === configId ? updated : c));
      return updated;
    } catch (error) {
      console.error('Failed to update config:', error);
      throw error;
    }
  };

  const deleteConfig = async (configId) => {
    try {
      await reminderService.deleteConfig(configId);
      setConfigs(configs.filter(c => c.id !== configId));
    } catch (error) {
      console.error('Failed to delete config:', error);
      throw error;
    }
  };

  const showReminder = useCallback((reminder) => {
    setActiveReminder(reminder);
  }, []);

  const dismissReminder = useCallback(async (instanceId) => {
    try {
      await reminderService.dismissReminder(instanceId);
      setActiveReminder(null);
    } catch (error) {
      console.error('Failed to dismiss reminder:', error);
    }
  }, []);

  const snoozeReminder = useCallback(async (instanceId, duration = null) => {
    try {
      await reminderService.snoozeReminder(instanceId, duration);
      setActiveReminder(null);
    } catch (error) {
      console.error('Failed to snooze reminder:', error);
    }
  }, []);

  const startTaskTracking = async (taskId, thresholdSeconds = 7200) => {
    try {
      const tracker = await reminderService.startTaskTracking(taskId, thresholdSeconds);
      setActiveTrackers([...activeTrackers, tracker]);
      
      // Set up periodic checks (every 15 minutes)
      const checkInterval = setInterval(async () => {
        try {
          const result = await reminderService.checkTaskDuration(taskId);
          if (result.reminder_triggered && result.instance) {
            showReminder(result.instance);
          }
        } catch (error) {
          // Silently fail for 404 errors
          if (error.response?.status !== 404) {
            console.error('Failed to check task duration:', error);
          }
        }
      }, 15 * 60 * 1000);

      // Store interval ID for cleanup
      tracker.checkIntervalId = checkInterval;
      
      return tracker;
    } catch (error) {
      // Silently fail for 404 errors (endpoint not implemented)
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Failed to start task tracking:', error);
      return null;
    }
  };

  const stopTaskTracking = async (taskId) => {
    try {
      await reminderService.stopTaskTracking(taskId);
      
      // Clear interval and remove from active trackers
      const tracker = activeTrackers.find(t => t.task_id === taskId);
      if (tracker && tracker.checkIntervalId) {
        clearInterval(tracker.checkIntervalId);
      }
      
      setActiveTrackers(activeTrackers.filter(t => t.task_id !== taskId));
    } catch (error) {
      // Silently fail for 404 errors (endpoint not implemented)
      if (error.response?.status === 404) {
        // Still clean up local state
        const tracker = activeTrackers.find(t => t.task_id === taskId);
        if (tracker && tracker.checkIntervalId) {
          clearInterval(tracker.checkIntervalId);
        }
        setActiveTrackers(activeTrackers.filter(t => t.task_id !== taskId));
        return;
      }
      console.error('Failed to stop task tracking:', error);
    }
  };

  const checkTaskDuration = async (taskId) => {
    try {
      const result = await reminderService.checkTaskDuration(taskId);
      if (result.reminder_triggered && result.instance) {
        showReminder(result.instance);
      }
      return result;
    } catch (error) {
      // Silently fail for 404 errors (endpoint not implemented)
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Failed to check task duration:', error);
      return null;
    }
  };

  const value = {
    activeReminder,
    preferences,
    configs,
    activeTrackers,
    initialized,
    updatePreferences,
    createConfig,
    updateConfig,
    deleteConfig,
    showReminder,
    dismissReminder,
    snoozeReminder,
    startTaskTracking,
    stopTaskTracking,
    checkTaskDuration,
    loadPreferences,
    loadConfigs,
  };

  return (
    <ReminderContext.Provider value={value}>
      {children}
    </ReminderContext.Provider>
  );
};
